import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, Loader, SaleAgreementModal } from "components";
import { ArrowIcon } from "icons";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { createElement, useEffect, useMemo, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { IPaymentFormFields, PaymentMethodsEnum } from "types";
import { mergeClasses } from "utils";
import { longPaymentFormSchema } from "./LongPaymentFormSchema";
import AccountInformationStep, {
  AccountInformationFormEnum,
} from "./steps/AccountInformationStep";
import ContactDetailsStep, {
  ContactDetailsFormEnum,
} from "./steps/ContactDetailsStep";
import { PaymentMethodFormEnum } from "./steps/PaymentMethodStep";

export enum LongPaymentFormFields {
  TermsAccepted = "termsAccepted",
}

interface PaymentFormValues {
  [AccountInformationFormEnum.Email]: string;
  [AccountInformationFormEnum.FirstName]: string;
  [AccountInformationFormEnum.LastName]: string;
  [ContactDetailsFormEnum.Country]: string;
  [ContactDetailsFormEnum.City]: string;
  [ContactDetailsFormEnum.Phone]: string;
  [ContactDetailsFormEnum.State]: string;
  [LongPaymentFormFields.TermsAccepted]: boolean;
  [PaymentMethodFormEnum.PaymentMethod]: PaymentMethodsEnum;
}

enum PaymentStepsEnum {
  AccountInformations = "account-informations",
  ContactDetails = "contact-details",
}

const stepsOrder = [
  PaymentStepsEnum.AccountInformations,
  PaymentStepsEnum.ContactDetails,
];

const steps = {
  [PaymentStepsEnum.AccountInformations]: {
    component: AccountInformationStep,
    title: "",
    fieldsEnum: AccountInformationFormEnum,
    isFooterShown: false,
  },
  [PaymentStepsEnum.ContactDetails]: {
    component: ContactDetailsStep,
    title: "Contact details",
    fieldsEnum: ContactDetailsFormEnum,
    isFooterShown: true,
  },
};

const DueToday: React.FC<{
  paymentForm: IPaymentFormFields;
  onModalOpen: () => void;
}> = ({ paymentForm, onModalOpen }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const termsAgreementValue = watch(LongPaymentFormFields.TermsAccepted);

  return (
    <div>
      <div className="mt-6 flex flex-row md:items-center justify-between w-full">
        <div>
          <h3 className="text-2xl">Due today</h3>
          <p className="text-gray-400 text-sm">Non-refundable downpayment</p>
        </div>
        <div>
          <p className="text-2xl md:text-[32px] md:leading-[1.1]">{`$${paymentForm.dueTodayPrice?.toLocaleString()}`}</p>
        </div>
      </div>
      <p className="text-sm mt-4 bg-light-gray p-4 rounded-[12px] leading-4">
        {paymentForm.dueTodayDescription}
      </p>
      <div className="mt-6">
        <Checkbox
          checked={termsAgreementValue}
          errors={errors}
          label={
            <>
              I agree to the&nbsp;
              <a
                className="underline cursor-pointer text-accent"
                onClick={onModalOpen}
                role="button"
              >
                Jetson ONE Pre-Order Sale Agreement
              </a>
              &nbsp;and&nbsp;
              <Link href="/privacy-policy" passHref>
                <a
                  className="underline cursor-pointer text-accent"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </Link>
              .
            </>
          }
          name={LongPaymentFormFields.TermsAccepted}
          onChange={(checked) =>
            setValue(LongPaymentFormFields.TermsAccepted, checked, {
              shouldValidate: true,
            })
          }
        />
      </div>
    </div>
  );
};

interface PaymentFormProps {
  paymentForm: IPaymentFormFields;
}

const LongPaymentForm: React.FC<PaymentFormProps> = ({ paymentForm }) => {
  const { push, query } = useRouter();
  const [currentStep, setCurrentStep] = useState(stepsOrder[0]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const currentStepIndex = useMemo(
    () => stepsOrder.findIndex((step) => step === currentStep),
    [currentStep]
  );
  const { fieldsEnum, title, component, isFooterShown } = useMemo(
    () => steps[currentStep],
    [currentStep]
  );

  const { method } = query;

  const formMethods = useForm<PaymentFormValues>({
    resolver: yupResolver(longPaymentFormSchema()),
    defaultValues: {
      [PaymentMethodFormEnum.PaymentMethod]: PaymentMethodsEnum.WireTransfer,
      [LongPaymentFormFields.TermsAccepted]: false,
    },
  });

  const paymentMethodValue = formMethods.watch(
    PaymentMethodFormEnum.PaymentMethod
  );

  const handlePlaceOrderByCard = async () => {
    try {
      if (!executeRecaptcha) {
        console.log("Execute recaptcha not yet available");
        return;
      }

      await executeRecaptcha("order");
      setFormError(null);
      setLoading(true);
      const values = formMethods.getValues();
      const res = await fetch("/api/payment/create-session", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      const { url: redirectUrl, id } = (await res.json()) ?? {};
      localStorage.setItem("checkoutSessionId", id);
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    } catch (err) {
      console.log(err);
      setFormError(
        "Something went wrong, please try again later or contact administrator"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrderByTransfer = async () => {
    try {
      if (!executeRecaptcha) {
        console.log("Execute recaptcha not yet available");
        return;
      }

      setFormError(null);
      setLoading(true);
      const { ...values } = formMethods.getValues();
      const token = await executeRecaptcha("order");
      const body = {
        ...values,
        token,
      };
      const res = await fetch("/api/order", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (res.status === 200) {
        push("/preorder/payment/success");
      }
    } catch (err) {
      console.error(err);
      setFormError(
        "Something went wrong, please try again later or contact administrator"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = async () => {
    const isValid = await formMethods.trigger(Object.values(fieldsEnum));
    const isLastStep = currentStepIndex === stepsOrder.length - 1;
    const isValidTerms = isLastStep
      ? await formMethods.trigger(LongPaymentFormFields.TermsAccepted)
      : true;

    if (!isValid || !isValidTerms) {
      return;
    }

    const currentIndex = stepsOrder.findIndex((s) => s === currentStep);
    if (currentIndex === stepsOrder.length - 1) {
      if (paymentMethodValue === PaymentMethodsEnum.Card) {
        return handlePlaceOrderByCard();
      }
      return handlePlaceOrderByTransfer();
    }

    push(`/preorder/payment/${stepsOrder[currentIndex + 1]}`, undefined, {
      shallow: true,
    });
    return setCurrentStep(stepsOrder[currentIndex + 1]);
  };

  const handlePrevStep = () => {
    const currentIndex = stepsOrder.findIndex((s) => s === currentStep);

    if (currentIndex === 0) {
      return push("/order#orderForm");
    }

    push(`/preorder/payment/${stepsOrder[currentIndex - 1]}`, undefined, {
      shallow: true,
    });
    return setCurrentStep(stepsOrder[currentIndex - 1]);
  };

  useEffect(() => {
    if (method) {
      formMethods.reset({
        paymentMethod:
          method === "card"
            ? PaymentMethodsEnum.Card
            : PaymentMethodsEnum.WireTransfer,
      });
      push(`/preorder/payment/${stepsOrder[0]}`, undefined, {
        shallow: true,
      });
    }
  }, [method, push, formMethods]);

  return (
    <div className="bg-white mx-auto max-w-[1480px] w-full pb-10">
      <Head>
        <title>Payment | Jetson - Personal Electric Aerial Vehicle</title>
      </Head>
      <SaleAgreementModal
        agreements={
          paymentForm?.agreementContent
            ? documentToReactComponents(paymentForm?.agreementContent)
            : null
        }
        isModalOpen={isModalOpen}
        setModalClose={() => setModalOpen(false)}
        title="Jetson ONE Pre-Order Sale Agreement"
      />
      <div className="flex justify-between w-full lg:gap-[63px] lg:flex-row flex-col">
        <div className="flex-2 rounded-[32px] w-full xl:min-w-[893px] h-[498px] lg:h-[527px] relative mr-4 lg:block hidden">
          <Image
            alt={paymentForm?.picture?.fields.file.fileName}
            className="w-full h-full !relative object-cover rounded-[16px] lg:rounded-[32px]"
            layout="fill"
            src={`https:${paymentForm?.picture?.fields.file.url}`}
          />
        </div>
        <div className="heading-2 text-center mt-6 lg:hidden block">
          <h3>Order your Jetson ONE</h3>
        </div>
        <div className="w-full mx-auto md:mx-0 my-10 md:my-0">
          <p className="flex items-center">
            <ArrowIcon className="rotate-180 mr-2" />
            <span
              className="text-[13px] hover:underline cursor-pointer font-medium uppercase"
              onClick={handlePrevStep}
              role="button"
            >
              Back
            </span>
          </p>
          <h3 className="text-[32px] leading-[110%] mt-5 mb-10">{title}</h3>
          <FormProvider {...formMethods}>
            {currentStep && createElement(component)}
            {isFooterShown && (
              <DueToday
                onModalOpen={() => setModalOpen(true)}
                paymentForm={paymentForm}
              />
            )}
          </FormProvider>
          {formError && (
            <p className="mt-5 text-red-500 text-sm">{formError}</p>
          )}
          <Button
            className={mergeClasses(
              "button-primary h-14 relative transition-all lg:mx-0 mx-auto mt-10 w-full lg:w-auto",
              loading
                ? "!pr-16 cursor-not-allowed opacity-70 pointer-events-none"
                : "pr-8"
            )}
            label={
              steps[stepsOrder[currentStepIndex + 1]]?.title
                ? `Continue to ${steps[stepsOrder[currentStepIndex + 1]].title}`
                : "Place order"
            }
            onClick={handleNextStep}
            rightIcon={
              loading
                ? () => <Loader className="left-auto right-3" />
                : ArrowIcon
            }
            rightIconClassName="text-accent ml-5 !w-6 !h-6"
          />
        </div>
      </div>
    </div>
  );
};

export default LongPaymentForm;
